import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { chatStreamPath } from '@/api/client';
import { queryKeys } from '@/api/queries';
import type { SseClientEvent } from '@/api/models';
import { openStream, type StreamHandle } from '@/lib/sse';
import { reportLeak } from '@/lib/telemetry';
import { useStreamStore } from './stream-store';

/**
 * Connects the chat's SSE stream to the stream store.
 *
 * The handshake that makes a page load gapless:
 *
 *   1. `GET .../chat` returns the thread *and* `cursor.stream_cursor`.
 *   2. We open the stream with that cursor as `Last-Event-ID`.
 *   3. The server replays everything after it, then goes live.
 *
 * Without step 1 handing over a cursor, anything emitted between the fetch
 * completing and the socket opening is lost - which on a busy run is exactly
 * the events a client is watching for.
 *
 * `stream_reset` is the other half: when the server cannot replay from our
 * cursor it says so rather than silently starting from somewhere. The store
 * drops its stream-derived state and bumps a refetch token, and this hook
 * re-fetches the chat and re-opens from the fresh cursor.
 */
export function useChatStream(projectId: number | null, initialCursor: string | null | undefined) {
  const queryClient = useQueryClient();
  const handleRef = useRef<StreamHandle | null>(null);
  const ingest = useStreamStore((s) => s.ingest);
  const setConnection = useStreamStore((s) => s.setConnection);
  const requestRefetch = useStreamStore((s) => s.requestRefetch);

  // Keep the newest cursor without re-opening the socket every time it moves.
  const cursorRef = useRef<string | null>(initialCursor ?? null);
  if (cursorRef.current === null && initialCursor) cursorRef.current = initialCursor;

  useEffect(() => {
    if (projectId === null) return;

    const handle = openStream<SseClientEvent>({
      path: chatStreamPath(projectId),
      initialCursor: cursorRef.current,
      onEvent: (payload, frame) => {
        cursorRef.current = frame.id ?? cursorRef.current;
        ingest(payload, frame.id);
      },
      onPhaseChange: (phase, detail) => setConnection(phase, detail),
      onResetRequested: () => {
        cursorRef.current = null;
        requestRefetch();
        void queryClient.invalidateQueries({ queryKey: queryKeys.chat(projectId) });
      },
      onMalformedFrame: (frame) => {
        reportLeak({ kind: 'render_error', token: 'malformed_sse_frame', context: { event: frame.event } });
      },
    });

    handleRef.current = handle;
    return () => {
      handle.close();
      handleRef.current = null;
    };
    // Re-opening on cursor change would restart the socket constantly; the
    // handle keeps its own cursor and resumes from it internally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ingest, setConnection, requestRefetch, queryClient]);

  return handleRef;
}
